import { html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import { FirebaseEncode } from '../../modules/FirebaseEncode.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';
import '../input-element/input-element.js';
import '../katapult-switcher/katapult-switcher.js';

class KatapultAnchorSpecEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        #specSourceLabel {
          position: absolute;
          top: 0;
          right: 0;
          padding: 1.25em 1em;
          font-style: italic;
          color: var(--primary-text-color-faded);
          background: white;
        }
      </style>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes/anchor_spec" data="{{anchorSpecAttribute}}">
      </katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes/soil_type" data="{{soilTypeAttribute}}">
      </katapult-firebase-worker>

      <template is="dom-if" if="[[itemData.source]]">
        <div id="specSourceLabel">Source: [[itemData.source]]</div>
      </template>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <!-- Anchor Spec -->
          <template is="dom-if" if="[[equal(step.id, 'anchor_spec')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <input-element
                    label="Anchor Spec"
                    attribute_name="anchor_spec"
                    hide-attribute-name
                    value="[[_itemData.anchor_spec]]"
                    data-path="_itemData.anchor_spec"
                    on-value-changed="updateProperty"
                    model="[[anchorSpecAttribute]]"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  ></input-element>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Strength Type -->
          <template is="dom-if" if="[[equal(step.id, 'strength_type')]]" restamp>
            <katapult-switcher
              slot="body"
              items="[[strengthTypes]]"
              label-path="label"
              value-path="value"
              value="[[_itemData.strength_type]]"
              data-path="_itemData.strength_type"
              on-value-changed="updateProperty"
              disabled="[[!fullyEditable]]"
            ></katapult-switcher>
          </template>
          <!-- Combined Holding Strength -->
          <template is="dom-if" if="[[equal(step.id, 'combined_holding_strength')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Combined Holding Strength"
                    value="[[_itemData.combined_holding_strength]]"
                    data-path="_itemData.combined_holding_strength"
                    on-value-changed="updateProperty"
                    data-parse="int"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">lbf</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Rod Strength -->
          <template is="dom-if" if="[[equal(step.id, 'rod_strength')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Rod Strength"
                    value="[[_itemData.rod_strength]]"
                    data-path="_itemData.rod_strength"
                    on-value-changed="updateProperty"
                    data-parse="int"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                    <span slot="suffix">lbf</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Soil Types -->
          <template is="dom-if" if="[[equal(step.id, 'soil_types')]]" restamp>
            <paper-table rounded outline dividers overflow-hidden slot="body" style="min-width: 500px;">
              <paper-row grey short>
                <paper-cell style="gap: 8px;">
                  <material-icon icon="grass"></material-icon>
                  <div>Soil Type</div>
                </paper-cell>
                <paper-cell style="gap: 8px;">
                  <material-icon icon="fitness_center"></material-icon>
                  <div>Holding Strength</div>
                </paper-cell>
                <paper-cell icon></paper-cell>
              </paper-row>
              <template is="dom-repeat" items="[[getSoilTypesArray(_itemData.soil_types.*)]]" mutable-data>
                <paper-row>
                  <paper-cell input>
                    <input-element
                      label="Soil Type"
                      attribute_name="soil_type"
                      hide-attribute-name
                      value="[[item.soil_type]]"
                      data-path="_itemData.soil_types.$(model.item.$key).soil_type"
                      on-value-changed="updateSoilType"
                      model="[[soilTypeAttribute]]"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    ></input-element>
                  </paper-cell>
                  <paper-cell input>
                    <paper-input
                      label="Holding Strength"
                      value="[[item.holding_strength]]"
                      data-path="_itemData.soil_types.$(model.item.$key).holding_strength"
                      on-value-changed="updateProperty"
                      data-parse="int"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    >
                      <span slot="suffix">lbf</span>
                    </paper-input>
                  </paper-cell>
                  <paper-cell icon-button>
                    <katapult-button
                      iconOnly
                      noBorder
                      icon="delete"
                      data-path="_itemData.soil_types.$(model.item.$key)"
                      data-value="$null"
                      on-click="updateProperty"
                    ></katapult-button>
                  </paper-cell>
                </paper-row>
              </template>
              <paper-row ripple light-grey short pointer on-click="addSoilType">
                <paper-cell style="gap: 8px;" justify-center>
                  <material-icon icon="add_circle"></material-icon>
                  <div>Add Soil Type</div>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      strengthTypes: {
        type: Array,
        value: () => [
          { value: 'rod_and_soil_holding_strengths', label: 'Rod and Soil Holding Strengths' },
          { value: 'combined_holding_strength', label: 'Combined Holding Strength' }
        ]
      }
    };
  }

  constructor() {
    super();
    this.computedLabel = {
      dependencies: [{ path: 'anchor_spec' }],
      compute: (spec) => spec ?? 'Untitled Anchor Spec'
    };
  }

  ready() {
    super.ready();

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'anchor_spec',
        title: 'Anchor Spec',
        trigger: () => this.get('_itemData.anchor_spec') != null
      },
      {
        id: 'strength_type',
        title: 'Strength Type',
        trigger: () => this.get('_itemData.strength_type') != null
      },
      {
        id: 'combined_holding_strength',
        title: 'Combined Holding Strength',
        trigger: () => this.get('_itemData.combined_holding_strength') != null,
        if: () => this.get('_itemData.strength_type') == 'combined_holding_strength'
      },
      {
        id: 'rod_strength',
        title: 'Rod Strength',
        trigger: () => this.get('_itemData.rod_strength') != null,
        if: () => this.get('_itemData.strength_type') == 'rod_and_soil_holding_strengths'
      },
      {
        id: 'soil_types',
        title: 'Soil Types',
        trigger: () => this.get('_itemData.soil_types') != null,
        if: () => this.get('_itemData.strength_type') == 'rod_and_soil_holding_strengths'
      }
    ]);
  }

  updateSoilType(e) {
    if (this.getSoilTypesArray().find((x) => x.soil_type == e.detail.value)) e.currentTarget.value = '';
    else this.updateProperty(e);
  }

  getSoilTypesArray() {
    return this.getItemsArray(this.get('_itemData.soil_types')).sort((a, b) => {
      if (!a?.soil_type) return 1;
      if (!b?.soil_type) return -1;
      return (a.soil_type ?? '').localeCompare(b.soil_type);
    });
  }

  addSoilType(e) {
    this.deepSet(`_itemData.soil_types.${GeneratePushID()}`, { soil_type: '', holding_strength: '' });
  }

  beforeSave() {
    if (this._itemData?.strength_type == 'rod_and_soil_holding_strengths') {
      delete this._itemData.combined_holding_strength;
    } else if (this._itemData?.strength_type == 'combined_holding_strength') {
      delete this._itemData.rod_strength;
      delete this._itemData.soil_types;
    }
    this._itemData.source = `Manual Entry - ${katapultAuth.user.email} (${new Date().toLocaleDateString()})`;
  }
}

customElements.define('katapult-anchor-spec-editor', KatapultAnchorSpecEditor);
